import {
  EmptyState,
  EmptyStateTitle,
  FormattedRelativeTime,
  Stack,
  Text,
  Box,
  InlineStack,
  PlainLink,
} from '@customerio/pluma-components/react';
import { useRouter } from '@tanstack/react-router';
import { isEmptyString } from '@/utils/string';
import type { BackfillComputed } from '@/features/tasks';
import { TaskStateBadge } from '@/features/tasks';
import * as styles from './tasks-list.css';

export interface TasksListProps {
  tasks: BackfillComputed[];
  isLoading: boolean;
  workspaceId: number | null | undefined;
  onClose: () => void;
}

export function TasksList({ tasks, isLoading, workspaceId, onClose }: TasksListProps) {
  const router = useRouter();

  if (isLoading) {
    return (
      <Box p="200" display="flex" justifyContent="center">
        <Text color="secondary">Loading tasks…</Text>
      </Box>
    );
  }

  if (tasks.length === 0) {
    return (
      <EmptyState>
        <EmptyStateTitle icon="check-circle">No new tasks</EmptyStateTitle>
      </EmptyState>
    );
  }

  return (
    <Stack gap="0" overflowY="auto" className={styles.scrollContainer}>
      {tasks.map((task) => {
        const lastUpdate = task.completed ?? task.started ?? task.created;
        const hasObjectName = !isEmptyString(task.object_name);
        const hasObjectId = task.object_id != null;
        const displayText = hasObjectName ? task.object_name : task.object_id;

        return (
          <PlainLink
            href={
              router.buildLocation({
                to: '/workspaces/$workspaceId/health',
                params: { workspaceId },
                search: { expand: task.id, tab: 'data', scrollTo: 'tasks' },
              }).href
            }
            key={task.id}
            p="200"
            className={styles.taskItem}
            onClick={onClose}
          >
            <Stack gap="050">
              <InlineStack gap="050" alignBlock="center">
                <FormattedRelativeTime date={lastUpdate} size="xs" color="secondary" />
                <TaskStateBadge state={task.state} />
              </InlineStack>
              <Text size="sm" weight="semibold">
                {task.info.label}
              </Text>

              {!isEmptyString(task.info.description) ? (
                <Text size="xs" color="secondary">
                  {task.info.description}
                </Text>
              ) : hasObjectName || hasObjectId ? (
                <Text size="xs" color="secondary">
                  {displayText}
                  {task.object_deleted === true ? ' (deleted)' : ''}
                </Text>
              ) : null}
            </Stack>
          </PlainLink>
        );
      })}
    </Stack>
  );
}
